import React, { useState } from 'react'
import Link from 'next/link'
import { AnimatedButton } from '@/components/ui/animated-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Logo } from '@/components/logo'
import { useMutation } from 'convex/react'
import { api } from 'socialloop-convex/api'
import confetti from 'canvas-confetti'
import { 
  Mail,
  Coffee,
  Zap,
  Heart,
  Code
} from 'lucide-react'
import { Github, Twitter, Linkedin } from "@/components/ui/brand-icons"

const companyLinks = [
    { href: '/about', label: 'About Favour' },
    { href: '/case-studies', label: 'Success Stories' },
    { href: '/blog', label: 'Blog' },
    { href: '/contact', label: 'Contact' },
]

const productLinks = [
    { href: '/features', label: 'Features' },
    { href: '/pricing', label: 'Pricing' },
    { href: '#', label: 'Demo' },
    { href: '#', label: 'Enterprise' },
]

const resourceLinks = [
    { href: '/blog', label: 'Blog & Insights' },
    { href: '#', label: 'Documentation' },
    { href: '#', label: 'API Reference' },
    { href: '#', label: 'Help Center' },
    { href: '#', label: 'Best Practices' },
]

const communityLinks = [
    { href: 'https://github.com', label: 'GitHub', icon: Github },
    { href: 'https://twitter.com', label: 'Twitter', icon: Twitter },
    { href: 'https://linkedin.com', label: 'LinkedIn', icon: Linkedin },
    { href: 'mailto:hello@socialloop.dev', label: 'Email', icon: Mail },
]

const footerLinks = [
    {
        name: 'Company',
        links: companyLinks,
    },
    {
        name: 'Product',
        links: productLinks,
    },
    {
        name: 'Resources',
        links: resourceLinks,
    },
]

// Design.json neubrutalism colors
const brandColors = [
    "bg-[#ffaf68]",     // Orange - warm and inviting
    "bg-[#70b5ff]",     // Blue - professional and trustworthy  
    "bg-[#79d45e]",     // Green - growth and success
    "bg-[#f4889a]",     // Pink - creative and friendly
    "bg-[#ba81ee]",     // Purple - innovative and premium
    "bg-[#4928fd]",     // Deep blue - authoritative
];

export default function Footer() {
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [message, setMessage] = useState('')
    const subscribeNewsletter = useMutation(api.newsletter.subscribe)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setMessage('')

        try {
            await subscribeNewsletter({ email, source: 'footer' })
            setMessage('Successfully subscribed!')
            setEmail('')
            // Trigger confetti celebration
            confetti({
                particleCount: 150,
                spread: 90,
                origin: { y: 0.8 },
                colors: ['#ffaf68', '#70b5ff', '#79d45e', '#f4889a', '#ba81ee']
            })
        } catch (error) {
            setMessage(error instanceof Error && /invalid/i.test(error.message) ? 'Please enter a valid email address.' : 'Subscription failed. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <footer className="relative border-t-8 border-black" style={{ backgroundColor: '#fdf1d3' }}>
            {/* Scattered decorative elements */}
            <div className="absolute top-4 left-10 w-6 h-6 bg-[#4928fd] border-2 border-black rounded-full"></div>
            <div className="absolute top-8 right-20 w-4 h-8 bg-[#79d45e] border-2 border-black transform rotate-45"></div>
            <div className="absolute bottom-4 left-1/3 w-5 h-5 bg-[#f4889a] border-2 border-black transform -rotate-12"></div>

            <div className="mx-auto max-w-7xl px-6 py-16">
                {/* Header Section with Personal Branding */}
                <div className="flex flex-wrap items-center justify-between gap-6 pb-12 border-b-4 border-black">
                    <div className="bg-[#ffaf68] border-4 border-black p-4 transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                        <Link href="/" aria-label="go home">
                            <Logo height={40} />
                        </Link>
                    </div>
                    
                    {/* Developer Badge */}
                    <div className="bg-white border-4 border-black p-4 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                        <div className="flex items-center gap-3">
                            <Code className="h-6 w-6 text-black" />
                            <div>
                                <p className="font-black text-black text-sm">Built with ❤️ by Favour</p>
                                <p className="font-semibold text-black/70 text-xs">Solo developer making networking easy</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Social Media Icons */}
                    <div className="flex gap-4">
                        {communityLinks.map((link, index) => {
                            const Icon = link.icon
                            const colorClass = brandColors[index % brandColors.length]
                            const rotateClass = index % 2 === 0 ? "rotate-1" : "-rotate-1"
                            
                            return (
                                <Link
                                    key={link.label}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={link.label}
                                    className={`${colorClass} border-4 border-black p-3 transform ${rotateClass} hover:rotate-0 hover:bg-black hover:text-white transition-all duration-200`}
                                >
                                    <Icon className="h-6 w-6" />
                                </Link>
                            )
                        })}
                    </div>
                </div>

                {/* Links Grid */}
                <div className="grid grid-cols-1 gap-8 md:grid-cols-4 py-12">
                    {footerLinks.map((linksGroup, index) => {
                        const colorClass = brandColors[index % brandColors.length];
                        const rotationClass = index % 2 === 0 ? "rotate-1" : "-rotate-1";
                        
                        return (
                            <div key={index} className={`${colorClass} border-4 border-black p-6 transform ${rotationClass} hover:rotate-0 transition-transform duration-200`}>
                                <h3 className="font-black text-xl text-black mb-6">{linksGroup.name.toUpperCase()}</h3>
                                <ul className="space-y-3">
                                    {linksGroup.links.map((link, linkIndex) => (
                                        <li key={linkIndex}>
                                            <Link
                                                href={link.href}
                                                className="font-bold text-black hover:bg-black hover:text-white px-2 py-1 inline-block border-2 border-transparent hover:border-black transition-all duration-200"
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                
                                {/* Decorative elements */}
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-black transform rotate-45"></div>
                                <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-black rounded-full"></div>
                            </div>
                        );
                    })}
                    
                    {/* Newsletter Section */}
                    <div className="bg-[#ba81ee] border-4 border-black p-6 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                        <h3 className="font-black text-xl text-black mb-4">STAY CONNECTED</h3>
                        <p className="text-black font-semibold text-sm mb-6">
                            Get networking insights, tips, and updates straight from Favour's development journey.
                        </p>

                        {/* Newsletter Form */}
                        <form onSubmit={handleSubmit} className="w-full">
                            <div className="space-y-4">
                                <Label className="block font-black text-black" htmlFor="email">
                                    NEWSLETTER
                                </Label>
                                <Input
                                    className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 focus:bg-[#ffaf68] focus:border-8 transition-all duration-200"
                                    placeholder="YOUR EMAIL"
                                    type="email"
                                    id="email"
                                    required
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isSubmitting}
                                />
                            </div>
                            <AnimatedButton
                                animation="ripple"
                                type="submit"
                                disabled={isSubmitting}
                                className="mt-4 bg-black text-white font-black border-4 border-black hover:bg-white hover:text-black transition-all duration-200 transform hover:rotate-2 disabled:opacity-50"
                            >
                                <span>{isSubmitting ? 'SUBSCRIBING...' : 'SUBSCRIBE NOW!'}</span>
                            </AnimatedButton>
                        </form>
                        
                        {/* Feedback Message */}
                        {message && (
                            <div className="bg-white border-2 border-black p-3 mt-4 transform -rotate-1">
                                <p className="text-xs font-black text-black">{message}</p>
                            </div>
                        )}
                        
                        {/* Fun developer note */}
                        <div className="bg-white border-2 border-black p-3 mt-6 transform -rotate-1">
                            <div className="flex items-center gap-2">
                                <Coffee className="h-4 w-4 text-black" />
                                <p className="text-xs font-black text-black">No spam, just good networking vibes!</p>
                            </div>
                        </div>
                        
                        {/* Decorative elements */}
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-black transform rotate-45"></div>
                        <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-black rounded-full"></div>
                    </div>
                </div>

                {/* Copyright Section */}
                <div className="pt-8 border-t-4 border-black">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="bg-[#4928fd] border-4 border-black px-6 py-3 transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                            <span className="font-black text-white text-lg">&copy; SOCIALLOOP 2025 - PRESENT</span>
                        </div>
                        
                        <div className="flex items-center gap-4 flex-wrap">
                            <Link
                                href="/privacy"
                                className="bg-white border-4 border-black px-4 py-2 font-black text-black hover:bg-black hover:text-white transform rotate-1 hover:rotate-0 transition-all duration-200"
                            >
                                PRIVACY
                            </Link>
                            <Link
                                href="/terms"
                                className="bg-white border-4 border-black px-4 py-2 font-black text-black hover:bg-black hover:text-white transform -rotate-1 hover:rotate-0 transition-all duration-200"
                            >
                                TERMS
                            </Link>
                        </div>
                        
                        {/* Developer signature */}
                        <div className="bg-[#79d45e] border-4 border-black px-6 py-3 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                            <div className="flex items-center gap-2">
                                <Heart className="h-4 w-4 text-black" />
                                <span className="font-black text-black">Made by Favour</span>
                                <Zap className="h-4 w-4 text-black" />
                            </div>
                        </div>
                    </div>
                    
                    {/* Fun footer message */}
                    <div className="mt-8 text-center">
                        <div className="bg-[#f4889a] border-4 border-black p-4 inline-block transform rotate-0.5 hover:rotate-0 transition-transform duration-200">
                            <p className="font-black text-black text-sm">
                                🚀 Turning awkward networking into meaningful connections, one event at a time!
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}
